import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/NotificationManagement.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IoIosNotifications } from "react-icons/io";
import { TbEdit } from "react-icons/tb";
import { RiDeleteBin6Line } from "react-icons/ri";
import API_URL from "../config";
import moment from "moment-timezone";
import { useAdmin } from "../contexts/AdminContext";

const NotificationManagement = () => {
  const { admin } = useAdmin();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const [scheduletitle, setScheduleTitle] = useState("");
  const [schedulebody, setScheduleBody] = useState("");
  const [scheduleTime, setScheduleTime] = useState(new Date());

  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSent, setLoadingSent] = useState(false);

  const [sendingNotification, setSendingNotification] = useState(false);

  const navigate = useNavigate();

  const fetchScheduledNotifications = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${API_URL}/scheduled/${admin?.branch}`);
      setScheduledNotifications(response.data);
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSentNotifications = async () => {
    setLoadingSent(true);
    try {
      const response = await axios.get(
        `${API_URL}/notification/${admin?.branch}`
      );

      setSentNotifications(response.data);
    } catch (error) {
      console.error(
        "Error fetching sent notifications:",
        error.response?.data || error.message
      );
    } finally {
      setLoadingSent(false);
    }
  };

  useEffect(() => {
    fetchSentNotifications();
    fetchScheduledNotifications();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSendingNotification(true);

    try {
      await axios.post(`${API_URL}/notification`, {
        title,
        body,
        branch: admin?.branch,
      });

      await axios.post(`${API_URL}/token/sendnotification`, {
        title,
        body,
        branch: admin?.branch,
      });

      alert("Notification sent successfully!");
      setTitle("");
      setBody("");
      fetchScheduledNotifications();
      fetchSentNotifications();
    } catch (error) {
      alert("Failed to send notification");
    } finally {
      setSendingNotification(false);
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();

    if (!scheduletitle || !schedulebody || !scheduleTime) {
      alert("Please fill in all fields.");
      return;
    }

    const istTime = moment.tz(scheduleTime, "Asia/Kolkata").format();

    const notificationData = {
      title: scheduletitle,
      body: schedulebody,
      scheduleTime: istTime,
      branch: admin?.branch,
    };

    try {
      const response = await axios.post(
        `${API_URL}/scheduled`,
        notificationData
      );

      alert("Notification scheduled successfully!");

      setScheduleTitle("");
      setScheduleBody("");
      setScheduleTime(new Date());
      fetchScheduledNotifications();
    } catch (error) {
      alert("Failed to schedule notification. Please try again.");
    }
  };

  const handleDeleteSentNotification = async (id) => {
    if (window.confirm("Are you sure you want to delete this notification?")) {
      try {
        await axios.delete(`${API_URL}/notification/${id}`);
        alert("Notification deleted successfully!");
        fetchSentNotifications();
      } catch (error) {
        alert("Failed to delete notification");
      }
    }
  };

  const formatDateTimeIST = (date) => {
    return moment(date).tz("Asia/Kolkata").format("DD/MM/YYYY h:mm A");
  };

  return (
    <div className="exerciseManagementContainer">
      <div className="exerciseManagementHeader">
        <div className="exerciseHeaderLeft">
          <IoIosNotifications
            size={30}
            color="#ff0000"
            style={{ marginRight: 10 }}
          />
          <h2>Notification Updates</h2>
        </div>
      </div>

      <div className="notificationMain">
        <h2 style={{ textAlign: "left" }}>Push Notification</h2>
        <form onSubmit={handleSubmit} className="notificationInputMain">
          <div className="addUserProfileInput">
            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={sendingNotification}
            />
          </div>
          <div className="addUserProfileInput" style={{ marginTop: 20 }}>
            <label>Description</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              placeholder="Enter Description"
              className="descNotify"
              disabled={sendingNotification}
            />
          </div>
          <div className="addUserBottomAggreementAndSaveButtonContainer">
            <button type="submit" disabled={sendingNotification}>
              {sendingNotification ? "Sending..." : "Send Notification"}
            </button>
          </div>
        </form>
      </div>

      <h2 style={{ textAlign: "left", marginTop: 40 }}>Sent Notifications:</h2>
      {loadingSent ? (
        <p>Loading sent notifications...</p>
      ) : (
        <div className="table-container">
          <table className="custom-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Date & Time (IST)</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {sentNotifications.length > 0 ? (
                sentNotifications.map((notification) => (
                  <tr key={notification._id}>
                    <td>{notification.title}</td>
                    <td>{notification.body}</td>
                    <td>{formatDateTimeIST(notification.createdAt)}</td>
                    <td className="deleteBtnGymTable">
                      <RiDeleteBin6Line
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          handleDeleteSentNotification(notification._id)
                        }
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    No notifications sent yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Scheduled Notification Form */}
      <div className="notificationMain" style={{ marginTop: 40 }}>
        <h2 style={{ textAlign: "left" }}>Automated Notifications</h2>
        <form onSubmit={handleScheduleSubmit} className="notificationInputMain">
          <div className="addUserProfileInput">
            <label>Title</label>
            <input
              type="text"
              placeholder="Enter title"
              value={scheduletitle}
              onChange={(e) => setScheduleTitle(e.target.value)}
              required
            />
          </div>
          <div className="addUserProfileInput" style={{ marginTop: 20 }}>
            <label>Description</label>
            <textarea
              placeholder="Enter Description"
              value={schedulebody}
              onChange={(e) => setScheduleBody(e.target.value)}
              className="descNotify"
              required
            />
          </div>
          <h3 style={{ margin: "20px 0 20px 0px" }}>Notification Schedule :</h3>
          <div className="dateAndTimeContainer" style={{ marginLeft: 0 }}>
            <DatePicker
              selected={scheduleTime}
              onChange={(date) => setScheduleTime(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={5}
              timeCaption="Time"
              dateFormat="dd/MM/yyyy h:mm aa"
              className="custom-datepicker"
            />
          </div>
          <div className="addUserBottomAggreementAndSaveButtonContainer">
            <button type="submit">Schedule Notification</button>
          </div>
        </form>
      </div>

      <h2 style={{ textAlign: "left", marginTop: 40 }}>
        Notification Scheduled:
      </h2>
      {loading ? (
        <p>Loading scheduled notifications...</p>
      ) : (
        <div className="table-container">
          <table className="custom-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Date & Time (IST)</th>
                <th>IsSend</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {scheduledNotifications.map((notification) => (
                <tr key={notification._id}>
                  <td>{notification.title}</td>
                  <td>{notification.body}</td>
                  <td>{formatDateTimeIST(notification.scheduleTime)}</td>
                  <td>{notification.isSent ? "Sended" : "Pending"}</td>
                  <td
                    className="editBtnGymTable"
                    onClick={() =>
                      navigate(
                        `/admin/notificationmanagement/${notification._id}`
                      )
                    }
                  >
                    <TbEdit style={{ cursor: "pointer" }} />
                  </td>
                  <td className="deleteBtnGymTable">
                    <RiDeleteBin6Line />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
